import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { first, Subscription, timer } from 'rxjs';
import { environment } from '../../../../environments/environment';

import { ChatService } from '../../services/chat.service';
import { OfferChatMessageView } from '../../models/offer-chat.type';
import { AppSnackbarService } from '../../../../core/notifications/snackbar.service';
import { MESSAGE_KEYS } from '../../../../shared/constants/messages';
import { UtilService } from '../../../../core/services/util.service';
import { ChatNotificationStore } from '../../../../store/chat-notification.store';
import { PermissionService } from '../../../../core/auth/permission.service';
import { AuthStore } from '../../../../store/auth.store';

@Component({
  selector: 'app-offer-chat',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  templateUrl: './offer-chat.component.html',
  styleUrl: './offer-chat.component.scss',
})
export class OfferChatComponent {
  private readonly hostRef = inject(ElementRef) as ElementRef<HTMLElement>;
  @ViewChild('messagesContainer')
  private readonly messagesContainerRef?: ElementRef<HTMLElement>;

  @ViewChild('chatInput')
  private readonly chatInputRef?: ElementRef<HTMLInputElement>;

  readonly companyId = input<string | null>(null);
  readonly bidId = input<string | null>(null);
  readonly collectId = input<string | null>(null);
  readonly autoFocusInput = input(false);
  /** When true, renders with a full-page header (carrier Messages tab).
   *  When false (default), renders compact for accordion use (shipper sidebar). */
  readonly isFullPage = input(false);

  private readonly chatService = inject(ChatService);
  private readonly snackbarService = inject(AppSnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly utilService = inject(UtilService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly chatNotificationStore = inject(ChatNotificationStore);
  private readonly permissionService = inject(PermissionService);
  private readonly authStore = inject(AuthStore);

  readonly isCarrier = this.permissionService.isCarrier;

  readonly placeholderText = computed(() =>
    this.isCarrier()
      ? this.translateService.instant('bids.sidebar.chat.placeholder_carrier')
      : this.translateService.instant('bids.sidebar.chat.placeholder'),
  );

  readonly messageDraft = signal('');
  readonly messages = signal<OfferChatMessageView[]>([]);
  readonly isLoading = signal(false);
  readonly isSending = signal(false);
  readonly isRefreshing = signal(false);

  readonly hasMessages = computed(() => this.messages().length > 0);
  readonly canSend = computed(
    () => this.messageDraft().trim().length > 0 && !this.isSending(),
  );

  private pollingSub: Subscription | null = null;
  private readonly resizeHandler = () => this.updateChatTopOffset();
  private lastConversationKey: string | null = null;

  constructor() {
    effect(
      () => {
        const conversationId = this.resolveConversationId();
        const type: 'spot' | 'contrato' = this.collectId() ? 'contrato' : 'spot';

        this.stopPolling();

        if (!conversationId) {
          this.messages.set([]);
          this.lastConversationKey = null;
          return;
        }

        this.lastConversationKey = conversationId;
        this.startPolling(conversationId, type);
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      if (!this.autoFocusInput()) {
        return;
      }

      const conversationId = this.resolveConversationId();
      if (!conversationId) {
        return;
      }

      this.focusMessageInput();
    });

    effect(() => {
      const hasConversation = Boolean(this.resolveConversationId());
      if (!hasConversation || this.isFullPage()) {
        return;
      }

      queueMicrotask(() => this.updateChatTopOffset());
    });

    this.destroyRef.onDestroy(() => {
      this.stopPolling();
      window.removeEventListener('resize', this.resizeHandler);
    });

    // measure and set CSS variable on init and listen to resizes
    // schedule after microtask so DOM is ready
    queueMicrotask(() => {
      this.updateChatTopOffset();
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    });
  }

  onDraftInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.messageDraft.set(target?.value ?? '');
  }

  sendMessage(): void {
    const content = this.messageDraft().trim();
    if (!content || this.isSending()) {
      return;
    }

    const user = this.getCurrentUser();
    const originId = this.coerceIdForPayload(user?.clientId ?? null);
    const destinationId = this.coerceIdForPayload(this.companyId());
    const conversationId = this.resolveConversationId();
    const conversationIdForPayload = this.coerceIdForPayload(conversationId);
    const isContract = Boolean(this.collectId());

    if (!originId || !destinationId || !conversationId || !conversationIdForPayload) {
      this.snackbarService.error(
        this.translateService.instant(MESSAGE_KEYS.HTTP.BAD_REQUEST),
      );
      return;
    }


    this.isSending.set(true);
    this.chatService
      .sendMessage({
        origem: originId,
        destino: destinationId,
        mensagem: content,
        ...(isContract ? { coleta: conversationIdForPayload } : { lance: conversationIdForPayload }),
      })
      .pipe(first())
      .subscribe({
        next: () => {
          this.messageDraft.set('');
          this.isSending.set(false);
          this.fetchMessages(conversationId, isContract ? 'contrato' : 'spot');
        },
        error: (err: unknown) => {
          const backendMsg = this.utilService.extractErrorMessage(err);
          this.snackbarService.error(
            backendMsg ?? this.translateService.instant(MESSAGE_KEYS.HTTP.SERVER_ERROR),
          );
          this.isSending.set(false);
        },
      });
  }

  focusMessageInput(): void {
    const maxAttempts = 12;
    const intervalMs = 75;
    let attempts = 0;

    const tryFocus = () => {
      attempts += 1;
      const input = this.chatInputRef?.nativeElement;
      if (input) {
        input.focus();
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(tryFocus, intervalMs);
      }
    };

    setTimeout(tryFocus, 0);
  }

  /** Manually refreshes messages without resetting the polling timer. */
  refresh(): void {
    const conversationId = this.resolveConversationId();
    if (!conversationId || this.isRefreshing()) return;
    const type: 'spot' | 'contrato' = this.collectId() ? 'contrato' : 'spot';
    this.isRefreshing.set(true);
    this.chatService
      .getMessages(conversationId, type)
      .pipe(first())
      .subscribe({
        next: (msgs) => {
          this.messages.set(this.mapMessages(msgs));
          this.isRefreshing.set(false);
          queueMicrotask(() => this.scrollToBottom());
          this.chatNotificationStore.startPolling();
        },
        error: () => this.isRefreshing.set(false),
      });
  }

  private startPolling(conversationId: string, type: 'spot' | 'contrato'): void {
    this.isLoading.set(true);
    this.pollingSub = timer(0, 5000).subscribe(() => {
      this.fetchMessages(conversationId, type);
    });
  }

  private fetchMessages(conversationId: string, type: 'spot' | 'contrato'): void {
    if (conversationId !== this.lastConversationKey) {
      return;
    }

    this.chatService
      .getMessages(conversationId, type)
      .pipe(first())
      .subscribe({
        next: (messages) => {
          this.messages.set(this.mapMessages(messages));
          this.isLoading.set(false);
          queueMicrotask(() => this.scrollToBottom());
          this.chatNotificationStore.startPolling();
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  private mapMessages(items: Record<string, unknown>[]): OfferChatMessageView[] {
    const user = this.getCurrentUser();
    const userName = user?.name?.toLowerCase() ?? '';

    return items.map((item, index) => {
      const senderName = this.asString(item['origemNome']) || this.asString(item['usuarioOrigem']) || '';
      const lowerSenderName = senderName.toLowerCase();
      const destinationName = this.asString(item['destinoNome']);
      const fallbackSenderName = this.asString(
        this.translateService.instant('bids.sidebar.chat.unknown_sender'),
      );
      const currentUserSender: boolean =
        this.asString(item['origem']) === user?.clientId
        || (Boolean(userName) && lowerSenderName.includes(userName));

      const currentUserLogo = this.buildS3AssetUrl(user?.image ?? null);
      const messageSenderLogo = this.extractMessageSenderLogo(item);

      return {
        id: this.asString(item['id']) || `${index}`,
        content: this.asString(item['mensagem']),
        senderName: senderName || destinationName || fallbackSenderName,
        senderLogo: currentUserSender ? currentUserLogo : messageSenderLogo,
        senderInitials: this.buildInitials(senderName || destinationName || ''),
        createdAtLabel: this.formatDate(item['dataCriacao']),
        isSentByCurrentUser: currentUserSender,
        readAtLabel: this.formatDate(item['dataLeitura']),
      };
    });
  }

  private getCurrentUser(): { clientId: string; name: string; image: string | null } | null {
    const user = this.authStore.currentUser() ?? this.readUserFromStorage();
    if (!user) return null;

    const backendUser = user as Record<string, unknown>;
    const client = (backendUser['client'] as Record<string, unknown> | null)
      ?? (backendUser['cliente'] as Record<string, unknown> | null)
      ?? null;

    return {
      clientId: this.asString(client?.['id']),
      name: this.asString(backendUser['name'] ?? backendUser['nome']),
      image: this.asString(backendUser['image'] ?? backendUser['imagem']) || null,
    };
  }

  private readUserFromStorage(): Record<string, unknown> | null {
    const storageKeys = ['auth_user', 'usuario'];

    for (const key of storageKeys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) {
          continue;
        }

        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object') {
          return parsed as Record<string, unknown>;
        }
      } catch {
        // Ignore malformed localStorage payloads and try the next key.
      }
    }

    return null;
  }

  private extractMessageSenderLogo(message: Record<string, unknown>): string | null {
    const directCandidates: unknown[] = [
      message['origemImagem'],
      message['imagemOrigem'],
      message['logoOrigem'],
      message['origemLogo'],
      message['senderImage'],
      message['senderLogo'],
      message['imagem'],
      message['logo'],
    ];

    for (const candidate of directCandidates) {
      const logoUrl = this.buildS3AssetUrl(candidate);
      if (logoUrl) {
        return logoUrl;
      }
    }

    const nestedCandidates: (Record<string, unknown> | null)[] = [
      (message['origemEmpresa'] as Record<string, unknown> | null) ?? null,
      (message['empresaOrigem'] as Record<string, unknown> | null) ?? null,
      (message['origem'] as Record<string, unknown> | null) ?? null,
      (message['empresa'] as Record<string, unknown> | null) ?? null,
    ];

    for (const candidate of nestedCandidates) {
      if (!candidate) {
        continue;
      }

      const nestedLogo = this.buildS3AssetUrl(
        candidate['logo'] ?? candidate['imagem'] ?? candidate['image'],
      );
      if (nestedLogo) {
        return nestedLogo;
      }
    }

    return null;
  }

  private buildS3AssetUrl(value: unknown): string | null {
    const image = this.asString(value).trim();
    if (!image) {
      return null;
    }

    if (image.startsWith('http') || image.includes(environment.AWS_S3_ASSETS)) {
      return image;
    }

    return `${environment.AWS_S3_ASSETS}${image}`;
  }

  private buildInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) ?? '';
    const second = parts[1]?.charAt(0) ?? '';
    const value = `${first}${second}`.toUpperCase();
    return value || 'CH';
  }

  private formatDate(value: unknown): string {
    if (typeof value !== 'string' || !value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  }

  private asString(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number') {
      return String(value);
    }

    return '';
  }

  private normalizeId(value: string | null): string | null {
    const normalized = String(value ?? '').trim();
    return normalized || null;
  }

  private coerceIdForPayload(value: string | null): string | number | null {
    const normalized = this.normalizeId(value);
    if (!normalized) {
      return null;
    }

    if (/^\d+$/.test(normalized)) {
      const asNumber = Number(normalized);
      if (Number.isFinite(asNumber)) {
        return asNumber;
      }
    }

    return normalized;
  }

  private resolveConversationId(): string | null {
    return this.normalizeId(this.bidId()) ?? this.normalizeId(this.collectId());
  }

  private scrollToBottom(): void {
    const element = this.messagesContainerRef?.nativeElement;
    if (!element) {
      return;
    }

    element.scrollTop = element.scrollHeight;
  }

  private stopPolling(): void {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
  }

  /** Measure distance from viewport top to chat host and set CSS var to allow
   *  max-height calculation that fills remaining viewport while leaving 1rem gap. */
  private updateChatTopOffset(): void {
    // In full-page mode the layout is handled entirely by CSS flex — no JS measurement needed.
    if (this.isFullPage()) {
      return;
    }

    try {
      const el = this.hostRef?.nativeElement;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const top = Math.max(0, Math.floor(rect.top));
      // measure header and input heights if present
      const headerEl = el.querySelector('.chat-header') as HTMLElement | null;
      const inputWrapEl = el.querySelector('.chat-input-wrap') as HTMLElement | null;
      const headerH = headerEl ? Math.ceil(headerEl.getBoundingClientRect().height) : 44;
      const inputH = inputWrapEl ? Math.ceil(inputWrapEl.getBoundingClientRect().height) : 56;

      // compute 1rem in pixels
      const fontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
      const bottomGap = Math.ceil(fontSize * 1); // 1rem gap

      // extra paddings inside card (margins/paddings around chat area)
      const extra = 20; // px

      const available = Math.max(
        0,
        Math.floor(window.innerHeight - top - bottomGap - headerH - inputH - extra),
      );

      // set CSS vars on host: measured top and computed chat height
      el.style.setProperty('--offer-chat-top', `${top}px`);
      el.style.setProperty('--chat-height', `${available}px`);
    } catch {
      // ignore measurement errors
    }
  }
}
